import { signal, SignalValue } from "omi";

/**
 * Storage type to use for persisting signal data
 */
type StorageType = "local" | "session";

/**
 * Configuration options for storage signal
 * @template T The type of value stored in the signal
 */
type SignalOptions<T> = {
  /** Default value if no stored value exists */
  default: T;
  /** Storage type to use - either localStorage or sessionStorage */
  storageType?: StorageType;
};

/**
 * Creates a signal that persists its value to either localStorage or sessionStorage
 * @template T The type of value stored in the signal
 * @param key Storage key to use for persisting the value
 * @param options Configuration options including default value and storage type
 * @returns A signal that automatically persists changes to web storage
 */
export function storageSignal<T>(key: string | undefined, options: SignalOptions<T>) {
  const storageType = options.storageType ?? "local";

  if (!hasStorage(storageType) || key == null) {
    return signal<T>(options.default);
  }

  const storage = storageType === "local" ? localStorage : sessionStorage;
  const initialState = storageSignalInitialState(key, options.default, storage);
  const s = signal<T>(initialState);

  const handler: ProxyHandler<SignalValue<T>> = {
    get: (target, p, receiver) => {
      if (p === "value") {
        return s.value;
      }
      return Reflect.get(target, p, receiver);
    },
    set: (target, p, value, receiver): boolean => {
      if (p === "value") {
        if (value == null) {
          storage.removeItem(storageKey(key));
          s.value = value;
          return true;
        }

        storage.setItem(storageKey(key), JSON.stringify(value));
        s.value = value;
        return true;
      }

      return Reflect.set(target, p, value, receiver);
    },
  };

  return new Proxy(s, handler);
}

/**
 * Gets the initial state for a storage signal from web storage
 * @template T The type of value stored in the signal
 * @param key Storage key to look up
 * @param defaultValue Default value if no stored value exists
 * @param storage Storage interface to use (localStorage or sessionStorage)
 * @returns The stored value or the default value
 */
function storageSignalInitialState<T>(key: string, defaultValue: T, storage: Storage): T {
  const storedValue = storage.getItem(storageKey(key));
  return storedValue ? JSON.parse(storedValue) : defaultValue;
}

/**
 * Prefixes a storage key to avoid collisions
 * @param key Base storage key
 * @returns Prefixed storage key
 */
function storageKey(key: string): string {
  return `ls_${key}`;
}

/**
 * Checks if the specified storage type is available and writable
 * @param type Storage type to check
 * @returns True if storage is available and writable
 */
function hasStorage(type: StorageType): boolean {
  try {
    const storage = type === "local" ? localStorage : sessionStorage;

    const hasReadWritePermission = (() => {
      storage.setItem("test", "test");
      storage.removeItem("test");
      return true;
    })();

    return typeof window !== "undefined" && storage != null && hasReadWritePermission;
  } catch (error) {
    return false;
  }
}
